import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { SettlementPanel } from "@/pages/admin/SettlementPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ManifestView from "@/components/ManifestView";
import PrintButtons from "@/components/PrintButtons";
import { ConfirmButton } from "@/components/Ui";
import { api, errMsg } from "@/lib/api";

export default function ManifestDialog({ trip, onClose, onChanged, canRelease = true }) {
  const [m, setM] = useState(null);
  const [wbBusy, setWbBusy] = useState(false);
  useEffect(() => {
    setM(null);
    if (trip) api.get(`/trips/${trip.id}/manifest`).then((r) => setM(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [trip]);

  const release = async () => {
    try {
      const { data } = await api.post(`/admin/trips/${trip.id}/dispatch`);
      setM(data);
      toast.success("Bus signed out - removed from booking, account slip generated");
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadWaybill = async () => {
    setWbBusy(true);
    try {
      const { data } = await api.post(`/admin/trips/${trip.id}/waybill`);
      setM((prev) => ({ ...prev, trip: { ...prev.trip, nrsa_waybill: data.nrsa_waybill } }));
      toast.success(data.reuploaded ? "Waybill re-uploaded to NRSA" : "Waybill uploaded to NRSA");
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setWbBusy(false); }
  };

  const wb = m?.trip?.nrsa_waybill;
  const isOpenTrip = m && ["scheduled", "boarding"].includes(m.trip.status);

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-4xl" data-testid="manifest-dialog">
        <DialogHeader><DialogTitle className="font-display">Passenger manifest & bus sign-out</DialogTitle></DialogHeader>
        {!m ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#0052FF]" /> : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <PrintButtons testPrefix="manifest-print">{(f) => <ManifestView m={m} format={f} />}</PrintButtons>
              {canRelease && isOpenTrip && wb?.uploaded && (
                <ConfirmButton size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" testid="release-bus-button" confirmLabel="Sign out bus"
                  title="Sign out this bus?" description={`The bus departs with ${m.passengers.length} of ${m.trip.capacity} seats filled. It is removed from booking, unpaid reservations expire, and the account slip splitting ${m.revenue ? "the fare revenue" : "revenue"} is generated.`} onConfirm={release}>
                  <LogOut className="h-4 w-4" />Sign out bus
                </ConfirmButton>
              )}
            </div>
            {canRelease && (isOpenTrip || wb?.uploaded) && (
              <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="nrsa-waybill-section">
                {wb?.uploaded ? (
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700" data-testid="waybill-status-uploaded">
                    <CheckCircle2 className="h-4 w-4" /> NRSA waybill uploaded · {wb.passengers} passenger{wb.passengers === 1 ? "" : "s"}{wb.uploaded_at ? ` · ${new Date(wb.uploaded_at).toLocaleString()}` : ""}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs font-semibold text-amber-700" data-testid="waybill-status-missing">
                    <AlertTriangle className="h-4 w-4" /> Upload the NRSA waybill to enable sign-out
                  </div>
                )}
                {isOpenTrip && (
                  <Button size="sm" variant="outline" className="ml-auto rounded-full border-[#0052FF]/30 text-[#0052FF] hover:bg-[#0052FF]/5" disabled={wbBusy} onClick={uploadWaybill} data-testid="upload-waybill-button">
                    {wbBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {wb?.uploaded ? "Re-upload waybill" : "Upload waybill to NRSA"}
                  </Button>
                )}
              </div>
            )}
            {m.settlement && canRelease && (
              <div className="space-y-2">
                <div className="label-xs">Sign-out account slip</div>
                <SettlementPanel s={m.settlement} onChanged={onChanged} />
              </div>
            )}
            <div className="overflow-x-auto rounded-2xl border border-slate-200 p-4"><ManifestView m={m} /></div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
